import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import api from "./api";

const METHOD_LABEL = {
  transferencia: "Transferencia bancaria",
  tarjeta_debito: "Tarjeta de débito",
  mercado_pago: "Mercado Pago",
  efectivo: "Efectivo",
};

function formatAmount(amount) {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(amount || 0);
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

async function fetchCardUrl(userId) {
  if (!userId) return null;
  try {
    const { data } = await api.get(`/users/${userId}/card/status`);
    if (data?.shared && data?.card_token) {
      return `${window.location.origin}/athlete/${data.card_token}`;
    }
  } catch {
    // sin permisos o sin card → ignorar
  }
  return null;
}

async function generateQrDataUrl(text) {
  try {
    return await QRCode.toDataURL(text, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 220,
      color: { dark: "#0A0A0A", light: "#FFFFFF" },
    });
  } catch {
    return null;
  }
}

/**
 * Renderiza UN comprobante en la página actual del `doc`. No agrega páginas
 * ni ejecuta `save()`. Extraído para poder reusarse en batch/consolidado.
 */
function _renderReceiptPage(doc, payment, user, qrDataUrl, pageInfo) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;

  // Header rojo
  doc.setFillColor(255, 59, 48);
  doc.rect(0, 0, pageW, 110, "F");

  // Brand
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.text("ATHLON SPORTS", margin, 54);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("TRAINING CONTROL · COMPROBANTE DE PAGO", margin, 72);

  // Nº comprobante chip
  doc.setFillColor(0, 0, 0);
  doc.rect(pageW - 180, 28, 140, 54, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text("COMPROBANTE Nº", pageW - 170, 44);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(payment.receipt_number || "—", pageW - 170, 66);

  let y = 150;
  doc.setTextColor(115, 115, 115);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`EMITIDO: ${formatDate(payment.created_at || new Date().toISOString())}`, margin, y);
  y += 30;

  // Atleta
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("ATLETA", margin, y);
  y += 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.text(payment.user_name || user?.name || "—", margin, y);
  y += 16;
  doc.setFontSize(10);
  doc.setTextColor(115, 115, 115);
  doc.text(payment.user_email || user?.email || "", margin, y);
  y += 30;

  doc.setDrawColor(38, 38, 38);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageW - margin, y);
  y += 24;

  const col1X = margin;
  const col2X = pageW / 2 + 12;

  function field(label, value, x, yy) {
    doc.setTextColor(115, 115, 115);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(label.toUpperCase(), x, yy);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(String(value || "—"), x, yy + 16);
  }

  field("Concepto", payment.concept || "Mensualidad Athlon Sports", col1X, y);
  field("Método de pago", METHOD_LABEL[payment.method] || payment.method || "—", col2X, y);
  y += 44;

  field("Fecha del pago", formatDate(payment.payment_date), col1X, y);
  field("Referencia", payment.reference || "Sin referencia", col2X, y);
  y += 50;

  // Monto destacado
  doc.setFillColor(10, 10, 10);
  doc.rect(margin, y, pageW - margin * 2, 90, "F");
  doc.setTextColor(163, 163, 163);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("TOTAL ABONADO", margin + 20, y + 28);
  doc.setTextColor(255, 59, 48);
  doc.setFontSize(36);
  doc.setFont("helvetica", "bold");
  doc.text(formatAmount(payment.amount), margin + 20, y + 70);
  y += 112;

  // Bloque Destinatario + QR
  const destBlockH = 96;
  const qrSize = 84;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text("DESTINATARIO DEL PAGO", margin, y);
  let destY = y + 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(`Titular: ${payment.bank_titular || "Athlon Sports"}`, margin, destY);
  destY += 16;
  doc.text(`Alias: ${payment.bank_alias || ""}`, margin, destY);
  destY += 16;
  doc.text(`CBU: ${payment.bank_cbu || ""}`, margin, destY);

  if (qrDataUrl) {
    const qrX = pageW - margin - qrSize;
    const qrY = y - 6;
    try {
      doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
      doc.setFontSize(7);
      doc.setTextColor(115, 115, 115);
      doc.setFont("helvetica", "bold");
      doc.text("ATHLETE CARD", qrX + qrSize / 2, qrY + qrSize + 10, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.text("Escaneá para ver mis marcas", qrX + qrSize / 2, qrY + qrSize + 20, { align: "center" });
    } catch {
      // si addImage falla, no rompemos el PDF
    }
  }
  y += destBlockH;

  // Estado
  const isApproved = (payment.status || "").toLowerCase() === "aprobado";
  doc.setFillColor(isApproved ? 50 : 255, isApproved ? 215 : 214, isApproved ? 75 : 10);
  doc.rect(margin, y, pageW - margin * 2, 40, "F");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("ESTADO: " + String(payment.status || "REGISTRADO").toUpperCase(), margin + 12, y + 17);
  doc.setFont("helvetica", "normal");
  doc.text(
    isApproved
      ? "Pago confirmado por el sistema. Este comprobante es definitivo."
      : "Este comprobante certifica la declaración del pago. Sujeto a verificación.",
    margin + 12, y + 30,
  );
  y += 56;

  // Footer
  const footerY = pageH - 40;
  doc.setDrawColor(38, 38, 38);
  doc.line(margin, footerY - 16, pageW - margin, footerY - 16);
  doc.setTextColor(115, 115, 115);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Athlon Sports · Training Control · ${payment.receipt_number || ""}`, margin, footerY);
  const pageLabel = pageInfo
    ? `Página ${pageInfo.pageNum} de ${pageInfo.totalPages}`
    : "Página 1 de 1";
  doc.text(pageLabel, pageW - margin, footerY, { align: "right" });
}

/**
 * Genera un PDF de comprobante de pago con branding Athlon Sports y descarga.
 * Si el atleta tiene su Athlete Card pública activa, embebe el QR al PDF.
 * @param {object} payment - Objeto pago del backend con todos los campos
 * @param {object} user - Usuario target (para mostrar nombre/email si el backend no los incluyó)
 */
export async function generatePaymentReceiptPDF(payment, user) {
  const cardUrl = await fetchCardUrl(payment.user_id || user?.user_id);
  const qrDataUrl = cardUrl ? await generateQrDataUrl(cardUrl) : null;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  _renderReceiptPage(doc, payment, user, qrDataUrl, { pageNum: 1, totalPages: 1 });
  doc.save(`Comprobante_${payment.receipt_number || "athlon"}.pdf`);
}

/**
 * Genera un PDF CONSOLIDADO con múltiples comprobantes (un comprobante por página).
 * Útil para entregar a un club, un padre/madre de familia, o un atleta que
 * necesite todos sus recibos juntos.
 * @param {Array} payments - Lista de pagos a incluir
 * @param {object} user - Usuario dueño de la mayoría de los pagos (para QR, nombre archivo)
 * @param {object} [opts]
 * @param {string} [opts.title="Comprobantes de pago"] - Título del archivo
 */
export async function generatePaymentBatchPDF(payments, user, opts = {}) {
  if (!Array.isArray(payments) || payments.length === 0) {
    throw new Error("No hay pagos para consolidar");
  }
  const cardUrl = await fetchCardUrl(user?.user_id || payments[0]?.user_id);
  const qrDataUrl = cardUrl ? await generateQrDataUrl(cardUrl) : null;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  payments.forEach((p, idx) => {
    if (idx > 0) doc.addPage();
    _renderReceiptPage(doc, p, user, qrDataUrl, { pageNum: idx + 1, totalPages: payments.length });
  });
  const stamp = new Date().toISOString().slice(0, 10);
  const clean = (user?.name || "athlon").replace(/\s+/g, "_");
  const suffix = opts.title ? `_${opts.title.replace(/\s+/g, "_")}` : "";
  doc.save(`Comprobantes_${clean}${suffix}_${stamp}.pdf`);
}
